import React, { useEffect, useState } from 'react';
import {
  Center,
  Loader,
  Text,
  Stack,
  Paper,
  Container
} from '@mantine/core';
import { initializeMsal, getMsalInitError } from '../../utils/msalInit';
import { MsalErrorFallback } from '../error/MsalErrorFallback';

interface MsalLoaderProps {
  children: React.ReactNode;
}

export const MsalLoader: React.FC<MsalLoaderProps> = ({ children }) => {
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  useEffect(() => {
    const initialize = async () => {
      try {
      
        // Try to initialize
        await initializeMsal();
        setInitializing(false);
      } catch (err) {
        console.error('Error initializing MSAL in loader:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setInitializing(false);
      }
    };

    initialize();
    
    // Set a timeout to ensure we don't show the loading state for too long
    const timeoutId = setTimeout(() => {
      if (initializing) {
        console.warn('MSAL initialization taking longer than expected');
      }
    }, 5000);
    
    return () => clearTimeout(timeoutId);
  }, [initializing]);

  // If there was an error, show the error fallback
  if (error || getMsalInitError()) {
    return <MsalErrorFallback error={error || getMsalInitError()} />;
  }

  // If we're initializing, show a loading spinner with animation
  if (initializing) {
    return (
      <Container size="sm" py="xl">
        <Paper
          p="xl"
          radius="md"
          shadow="md"
          withBorder
        >
          <Center style={{ width: '100%', padding: '2rem' }}>
            <Stack align="center" spacing="md">
              <Loader size="lg" color="blue" variant="bars" />
              <Text size="lg" weight={500}>
                Initializing authentication...
              </Text>
              <Text size="sm" color="dimmed" align="center">
                Please wait while we connect to Microsoft's authentication service
              </Text>
            </Stack>
          </Center>
        </Paper>
      </Container>
    );
  }

  // Otherwise, render the children
  return <>{children}</>;
}; 